/**
 * Analytics / telemetry consent (RODO).
 * Essential cookies (auth, guest limits) are out of scope — disclosed on /cookies.
 */

export const ANALYTICS_CONSENT_COOKIE = 'architectIqCookieConsent';

export type AnalyticsConsent = 'accepted' | 'declined' | 'unknown';

function readRawConsentCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${ANALYTICS_CONSENT_COOKIE}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function getAnalyticsConsent(): AnalyticsConsent {
  const raw = readRawConsentCookie();
  if (raw === 'true') return 'accepted';
  if (raw === 'false') return 'declined';
  return 'unknown';
}

/** Delete consent cookie so the banner can show again (cookie settings). */
export function clearAnalyticsConsentCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${ANALYTICS_CONSENT_COOKIE}=; Max-Age=0; path=/; SameSite=Lax`;
}

/**
 * Enable or disable optional trackers after user choice / restore on boot.
 * Updates Google Consent Mode before loading / disabling GA + Ads.
 */
export async function applyAnalyticsConsent(accepted: boolean): Promise<void> {
  const { initializeGA, disableGA, updateGoogleConsent } = await import('./gaEvents');
  const { enableAppInsights, disableAppInsights } = await import('./appInsights');

  // Consent Mode update queues on dataLayer even before deferred gtag.js lands.
  updateGoogleConsent(accepted);

  if (accepted) {
    // initializeGA → ensureGtagLoaded (do not fetch Google script on Decline).
    initializeGA();
    enableAppInsights();
  } else {
    disableGA();
    disableAppInsights();
  }
}

/** Call once on app boot — restore trackers if consent cookie already accepted. */
export async function restoreAnalyticsConsentFromCookie(): Promise<void> {
  if (getAnalyticsConsent() === 'accepted') {
    await applyAnalyticsConsent(true);
  }
}
